// Leitura da árvore de totais filtrada pela etapa exibida.
//
// A árvore persistida (`closingValuesTotal.byItemType.SUM`, `A.AIR[]`…) pode
// guardar leftover de categoria (RSVP em C sem serviço) e células cacheadas
// numa identidade que não tem linha nesta etapa. O export (Api MMV-2624) já
// descarta essas células; estes helpers fazem o mesmo na UI.
//
// `budgetServices === undefined` (list snapshot / fixture) devolve o
// agregado gravado — não há de onde reconstruir. Não faz parse de `|dup|`.

import { isTotalsIdentityVisibleInStage } from "./budget-stage-visibility";

export type TotalsVisibilityContext = {
  supplierList?: {
    supplierId?: string;
    calcOption?: string;
    deletedInVersions?: number[];
  }[];
  budgetServices?: {
    supplierId?: string;
    type?: string;
    calcOption?: string;
    versions?: number[];
  }[];
  addedItemTypeList?: {
    type?: string;
    supplierId?: string;
    calcOption?: string;
    deletedInVersions?: number[];
  }[];
  /**
   * Status da etapa lida (OPEN / NEGOTIATE / APPROVED). Sem ele a forma de
   * cobrança do serviço é a do orçamento e categoria movida no fechamento
   * some da soma (MMV-2626).
   */
  status?: string;
};

const CALC_OPTIONS = ["A", "B", "C", "D"] as const;

export function visibilityContextFromBudget(
  budget:
    | {
        supplierList?: TotalsVisibilityContext["supplierList"];
        budgetServices?: TotalsVisibilityContext["budgetServices"];
        addedItemTypeList?: TotalsVisibilityContext["addedItemTypeList"];
      }
    | undefined,
  status?: string
): TotalsVisibilityContext {
  return normalizeTotalsVisibility({
    supplierList: budget?.supplierList,
    budgetServices: budget?.budgetServices,
    addedItemTypeList: budget?.addedItemTypeList,
    status,
  });
}

/**
 * `[]` é fixture / orçamento ainda sem serviço — mesmo contrato de
 * `budgetServices === undefined`. O filtro por serviço só vale quando a
 * lista hidratada tem ao menos uma linha; leftover (RSVP em C) vive em
 * orçamentos que JÁ têm serviço em outra identidade.
 */
export function normalizeTotalsVisibility(
  ctx: TotalsVisibilityContext | undefined
): TotalsVisibilityContext {
  if (!ctx) return {};
  return {
    ...ctx,
    budgetServices:
      Array.isArray(ctx.budgetServices) && ctx.budgetServices.length > 0
        ? ctx.budgetServices
        : undefined,
  };
}

function cellIsVisible(
  item: { supplierId?: string; calcOption?: string; type?: string },
  category: string,
  calcOption: string,
  version: number,
  ctx: TotalsVisibilityContext
): boolean {
  return isTotalsIdentityVisibleInStage(
    item,
    category,
    calcOption,
    version,
    ctx.supplierList,
    ctx.budgetServices,
    ctx.addedItemTypeList,
    ctx.status
  );
}

/**
 * Filtra as folhas de uma categoria na árvore. Sem `budgetServices` (array)
 * devolve o array original — fixtures e list snapshot não mudam.
 */
export function filterVisibleCategoryTotals(
  items: any[] | undefined,
  category: string,
  calcOption: string,
  version: number,
  ctx: TotalsVisibilityContext | undefined,
  supplierId?: string
): any[] {
  if (!Array.isArray(items)) return [];
  const visibility = normalizeTotalsVisibility(ctx);

  return items.filter((curr) => {
    if (supplierId) {
      if (curr.supplierId && curr.supplierId !== supplierId) return false;
      if (!curr.supplierId && Array.isArray(visibility.budgetServices)) {
        return false;
      }
      if (!curr.supplierId) return true;
    }

    if (!Array.isArray(visibility.budgetServices)) {
      if (!visibility.addedItemTypeList) return true;
    }

    return cellIsVisible(curr, category, calcOption, version, visibility);
  });
}

/**
 * `SUM` do bloco recomposto a partir das células que sobraram no filtro.
 *
 * MMV-2659: o `SUM` gravado na árvore é do momento em que o total foi
 * calculado e continua contando a categoria excluída na etapa. Como o
 * cabeçalho do bloco e o card "Total" (BaseValuesTotal) leem o `SUM` direto,
 * eles mostravam a soma antiga enquanto a lista de categorias logo acima —
 * que é filtrada — mostrava outra (MM 23954.26: card "Total" com
 * Tarifas 1.646,33 e Imposto 230,02, contra 1.171,54 / 115,01 das três
 * categorias visíveis, porque a Cenografia excluída entrava duas vezes).
 *
 * O molde é o `SUM` gravado: cada campo `{ totalInBRL, totalInCurrency }` é
 * re-somado das células visíveis; o que não tiver esse formato (contadores,
 * moeda) é copiado como está.
 */
function recalculateBlockSum(storedSum: any, filteredBlock: Record<string, unknown>): any {
  if (!storedSum || typeof storedSum !== "object") return storedSum;

  const isAmount = (value: any) =>
    value &&
    typeof value === "object" &&
    (typeof value.totalInBRL === "number" ||
      typeof value.totalInCurrency === "number");

  const cells: any[] = [];
  for (const key of Object.keys(filteredBlock)) {
    if (key === "SUM") continue;
    const items = filteredBlock[key];
    if (Array.isArray(items)) cells.push(...items);
  }

  const next: Record<string, unknown> = {};
  for (const field of Object.keys(storedSum)) {
    if (!isAmount(storedSum[field])) {
      next[field] = storedSum[field];
      continue;
    }
    let totalInBRL = 0;
    let totalInCurrency = 0;
    for (const cell of cells) {
      totalInBRL += cell?.[field]?.totalInBRL || 0;
      totalInCurrency += cell?.[field]?.totalInCurrency || 0;
    }
    next[field] = { totalInBRL, totalInCurrency };
  }
  return next;
}

export function filterTotalsBlockToVisibleStage(
  block: any,
  calcOption: string,
  version: number,
  ctx: TotalsVisibilityContext
): any {
  const visibility = normalizeTotalsVisibility(ctx);
  if (!block || !Array.isArray(visibility.budgetServices)) return block;

  const next: Record<string, unknown> = {};
  for (const key of Object.keys(block)) {
    if (key === "SUM" || !Array.isArray(block[key])) {
      next[key] = block[key];
      continue;
    }
    next[key] = filterVisibleCategoryTotals(
      block[key],
      key,
      calcOption,
      version,
      visibility
    );
  }
  if (block.SUM) {
    next.SUM = recalculateBlockSum(block.SUM, next);
  }
  return next;
}

export function sumVisibleTotalsFromTree(
  tree: { byCalculationType?: any; byItemType?: any } | undefined,
  version: number,
  ctx?: TotalsVisibilityContext
): number {
  if (!tree) return 0;
  const visibility = normalizeTotalsVisibility(ctx);
  if (!Array.isArray(visibility.budgetServices)) {
    return tree.byItemType?.SUM?.totalInBRL || 0;
  }

  const byCalc = tree.byCalculationType;
  if (!byCalc) return tree.byItemType?.SUM?.totalInBRL || 0;

  let sum = 0;
  for (const calc of CALC_OPTIONS) {
    const block = byCalc[calc];
    if (!block) continue;
    for (const category of Object.keys(block)) {
      if (category === "SUM") continue;
      const items = block[category];
      if (!Array.isArray(items)) continue;
      for (const item of items) {
        if (cellIsVisible(item, category, calc, version, visibility)) {
          sum += item.totals?.totalInBRL || 0;
        }
      }
    }
  }
  return sum;
}

export function sumVisibleBlockField(
  block: any,
  calcOption: string,
  version: number,
  field: string,
  ctx: TotalsVisibilityContext
): number {
  if (!block) return 0;
  const visibility = normalizeTotalsVisibility(ctx);
  if (!Array.isArray(visibility.budgetServices)) {
    return block?.SUM?.[field]?.totalInBRL || 0;
  }

  let sum = 0;
  for (const category of Object.keys(block)) {
    if (category === "SUM") continue;
    const items = block[category];
    if (!Array.isArray(items)) continue;
    for (const item of items) {
      if (cellIsVisible(item, category, calcOption, version, visibility)) {
        sum += item[field]?.totalInBRL || 0;
      }
    }
  }
  return sum;
}

/**
 * Campo global (Imposto MM / Taxa MM) somado dos blocos JÁ filtrados.
 *
 * MMV-2659: ler `byCalculationType.SUM.SUM` direto trazia a categoria
 * excluída na etapa de volta — o agregado é do momento do cálculo e não tem
 * células por categoria de onde filtrar. Somando os quatro blocos, cada um
 * re-somado das células visíveis, o valor acompanha o que a tela lista.
 *
 * Nem toda árvore guarda esses campos por bloco: em orçamentos onde eles só
 * existem no agregado, nenhuma célula tem o campo e aí o agregado é a única
 * fonte possível — é o que o fallback preserva. A presença é verificada nas
 * células (não o valor), então zero legítimo continua sendo zero.
 */
export function sumVisibleGlobalField(
  byCalcType: any,
  version: number,
  field: string,
  ctx: TotalsVisibilityContext
): number {
  const visibility = normalizeTotalsVisibility(ctx);
  const aggregate = byCalcType?.SUM?.SUM?.[field]?.totalInBRL || 0;
  if (!byCalcType || !Array.isArray(visibility.budgetServices)) return aggregate;

  let fieldExistsInCells = false;
  let sum = 0;
  for (const calc of CALC_OPTIONS) {
    const block = byCalcType[calc];
    if (!block) continue;
    for (const category of Object.keys(block)) {
      if (category === "SUM") continue;
      const items = block[category];
      if (!Array.isArray(items)) continue;
      for (const item of items) {
        if (item?.[field] === undefined) continue;
        fieldExistsInCells = true;
        if (cellIsVisible(item, category, calc, version, visibility)) {
          sum += item[field]?.totalInBRL || 0;
        }
      }
    }
  }
  return fieldExistsInCells ? sum : aggregate;
}

export function resolveStageTotalsTree(params: {
  budgetValuesTotal: any;
  negociationValuesTotal: any;
  closingValuesTotal: any;
  status: string | undefined;
  version: number;
}): any {
  const {
    budgetValuesTotal,
    negociationValuesTotal,
    closingValuesTotal,
    status,
    version,
  } = params;

  if (status === "APPROVED") return closingValuesTotal;
  if (status === "NEGOTIATE" && version > 0) {
    return negociationValuesTotal?.[version - 1];
  }
  return budgetValuesTotal;
}

export function getVisibleStageGrandTotal(
  budget:
    | {
        budgetValuesTotal?: any;
        negociationValuesTotal?: any;
        closingValuesTotal?: any;
        supplierList?: TotalsVisibilityContext["supplierList"];
        budgetServices?: TotalsVisibilityContext["budgetServices"];
        addedItemTypeList?: TotalsVisibilityContext["addedItemTypeList"];
      }
    | undefined,
  status: string | undefined,
  version: number
): number {
  if (!budget) return 0;
  const tree = resolveStageTotalsTree({
    budgetValuesTotal: budget.budgetValuesTotal,
    negociationValuesTotal: budget.negociationValuesTotal,
    closingValuesTotal: budget.closingValuesTotal,
    status,
    version,
  });
  return sumVisibleTotalsFromTree(
    tree,
    version,
    visibilityContextFromBudget(budget, status)
  );
}
